import React, { useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Spinner,
} from "react-bootstrap";
import {
  BsFileEarmarkTextFill,
  BsLockFill,
  BsShieldFillCheck,
} from "react-icons/bs";
import { userServices } from "../../../APIServices/userServices";


const TwoFactorAuth = () => {
  const [OTP, setOTP] = useState(Array(6).fill(""));
  const [error, setError] = useState("");
  const [buttonLoader, setButtonLoader] = useState(false);

  const inputRefs = useRef([]);

  let authType = JSON.parse(localStorage.getItem("authType"));

  // ================= OTP VALUE =================
  const getOtpValue = () => OTP.join("");

  // ================= VERIFY OTP =================
  const twpFactorAuthenticationVerify = async () => {
    try {
      setButtonLoader(true);
      setError("");

      const enteredOtp = getOtpValue();

      if (enteredOtp.length !== 6) {
        setError("Please enter 6 digit OTP");
        return;
      }

      let endPoint =
        authType === "apps"
          ? userServices.verifyTwoFAForAppLogin
          : userServices.verifyTwoFAForEmailLogin;

      let userData = {
        otp: enteredOtp,
      };

      let data = await endPoint(userData);

      if (data?.statusCode === 200) {
        setOTP(Array(6).fill(""));
        localStorage.setItem("twoFA", JSON.stringify(false));

        // redirect or reload
        window.location.reload();
      } else {
        setError(data?.message || "Invalid OTP");
      }
    } catch (err) {
      console.log(err);
      setError("Something went wrong");
    } finally {
      setButtonLoader(false);
    }
  };

  // ================= OTP CHANGE =================
  const handleOtpChange = (value, index) => {
    const digit = value.replace(/\D/g, "").slice(-1);

    const updated = [...OTP];
    updated[index] = digit;

    setOTP(updated);
    setError("");

    // move next
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // ================= KEYBOARD =================
  const handleOtpKeyDown = (event, index) => {
    if (event.key === "Backspace" && !OTP[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (event.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (event.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // ================= PASTE OTP =================
  const handleOtpPaste = (event) => {
    event.preventDefault();

    const paste = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);

    if (!paste) return;

    const pasteDigits = paste.split("");
    const updated = Array(6).fill("");

    pasteDigits.forEach((digit, idx) => {
      if (idx < 6) updated[idx] = digit;
    });

    setOTP(updated);

    const focusIndex = pasteDigits.length >= 6 ? 5 : pasteDigits.length;

    inputRefs.current[focusIndex]?.focus();
  };

  // ================= LOGOUT =================
  const logout = async () => {
    await userServices.logout();
  };

  return (
    <Container fluid className="min-vh-100 p-0 bg-light">
      <Row className="g-0 min-vh-100">
        {/* LEFT SIDE */}
        <Col
          md={6}
          className="d-flex flex-column justify-content-between text-white px-5 py-4"
          style={{ backgroundColor: "#74B043" }}
        >
          {/* LOGO */}
          <div className="d-flex align-items-center gap-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle bg-white text-success fw-bold"
              style={{
                width: 48,
                height: 48,
                fontSize: 28,
              }}
            >
              G
            </div>

            <h4 className="mb-0 fw-bold">Galaxbiotech</h4>
          </div>

          {/* TEXT */}
          <div>
            <h1 className="fw-bold lh-base mb-3" style={{ fontSize: "3rem" }}>
              Secure your access <br />
              with 2FA Verification
            </h1>

            <p className="fs-5">
              Advanced protection for your clinical system.
            </p>
          </div>

          {/* SECURITY */}
          <div>
            <h5 className="fw-bold mb-3">Enterprise Security Standards</h5>

            <div className="d-flex align-items-center gap-2 mb-2">
              <BsShieldFillCheck size={18} />
              <span>HIPAA Compliant PHI Protection</span>
            </div>

            <div className="d-flex align-items-center gap-2 mb-2">
              <BsLockFill size={18} />
              <span>21 CFR Part 11 Electronic Records</span>
            </div>

            <div className="d-flex align-items-center gap-2">
              <BsFileEarmarkTextFill size={18} />
              <span>GDPR Data Sovereignty</span>
            </div>
          </div>
        </Col>

        {/* RIGHT SIDE */}
        <Col
          md={6}
          className="d-flex align-items-center justify-content-center bg-light"
        >
          <Card
            className="border-0 shadow rounded-4 px-4 py-4"
            style={{
              width: "100%",
              maxWidth: "420px",
            }}
          >
            <Card.Body>
              {/* TITLE */}
              <div className="text-center">
                <h3 className="fw-bold text-secondary">
                  2 Factor Authentication
                </h3>

                <p className="text-muted small">
                  {authType === "apps"
                    ? "(By Authenticator App)"
                    : "(By Email)"}
                </p>
              </div>

              {/* OTP */}
              <div className="text-center">
                <Form.Label className="fw-bold">Enter OTP</Form.Label>

                <div className="d-flex justify-content-center gap-2 mt-2">
                  {OTP.map((value, index) => (
                    <input
                      key={index}
                      type="text"
                      pattern="\d*"
                      maxLength="1"
                      className="border rounded text-center"
                      style={{
                        width: 40,
                        height: 40,
                        fontSize: 15,
                        borderColor: "#8BC34A",
                        outline: "none",
                      }}
                      value={value}
                      onChange={(e) => handleOtpChange(e.target.value, index)}
                      ref={(el) => (inputRefs.current[index] = el)}
                      onKeyDown={(e) => handleOtpKeyDown(e, index)}
                      onPaste={handleOtpPaste}
                      required
                    />
                  ))}
                </div>
              </div>

              {/* ERROR */}
              {error && (
                <div className="text-danger small text-center my-2">
                  {error}
                </div>
              )}

              {/* VERIFY BUTTON */}
              <div className="hstack gap-2 my-2">
                <Button
                  onClick={twpFactorAuthenticationVerify}
                  className="w-100 border-0"
                  style={{ backgroundColor: "#74B043" }}
                  disabled={buttonLoader}
                >
                  {buttonLoader ? (
                    <div className="hstack justify-content-center gap-2">
                      <Spinner size="sm" />
                      Verifying...
                    </div>
                  ) : (
                    "Verify"
                  )}
                </Button>
                <Button
                  onClick={logout}
                  variant="outline-danger"
                  className="w-100"
                >
                  Back
                </Button>
              </div>

              <hr className="my-4" style={{ borderColor: "#9CCC65" }} />

              <div
                className="rounded-3 p-3 small"
                style={{
                  backgroundColor: "#F3F5F7",
                  fontSize: "12px",
                  lineHeight: "1.6",
                }}
              >
                <p className="mb-3">
                  <strong>🍪 Cookie & Privacy Policy:</strong> We use strictly
                  necessary cookies for session management, security, and audit
                  trails required by <strong>HIPAA, GDPR,</strong> and{" "}
                  <strong>21 CFR Part 11</strong>.
                </p>
                <p className="mb-0">
                  By logging in, you agree to our{" "}
                  <strong>Terms of Service</strong> and{" "}
                  <strong>Privacy Policy</strong>.
                </p>
              </div>

              <p className="text-center small mt-4 mb-0 text-dark">
                Copyright© 2026 Galaxbiotech All Rights Reserved
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default TwoFactorAuth;
